export interface StudentThemeVisual {
  key: string;
  bodyMode: 'light' | 'dark';
  layoutClass: string;
  chatClass: string;
  accent: string;
  primary: string;
  surface: string;
  surfaceAlt: string;
  border: string;
  chip: string;
  mascot: 'rainbow' | 'mango' | 'rocket' | 'ice-cream' | 'default';
}

const THEME_ALIAS_MAP: Record<string, string> = {
  custom1: 'default',
  none: 'default',
  standard: 'default',
  default: 'default',
  learninglane: 'learning_lane',
  'learning lane': 'learning_lane',
  rainbow: 'rainbow',
  rainbow_theme: 'rainbow',
  rainbowtheme: 'rainbow',
  mango: 'mango',
  mango_theme: 'mango',
  mangotheme: 'mango',
  rocket: 'rocket',
  rocket_theme: 'rocket',
  rockettheme: 'rocket',
  icecream: 'ice_cream',
  'ice cream': 'ice_cream',
  ice_cream: 'ice_cream',
  ice_cream_theme: 'ice_cream',
  icecreamtheme: 'ice_cream'
};

const DEFAULT_VISUAL: StudentThemeVisual = {
  key: 'default',
  bodyMode: 'light',
  layoutClass: 'student-layout-default',
  chatClass: 'student-chat-default',
  accent: '#7f5ba7',
  primary: '#5d3e8c',
  surface: '#ffffff',
  surfaceAlt: '#f7f3fb',
  border: 'rgba(151, 130, 181, 0.22)',
  chip: 'rgba(245, 239, 251, 0.92)',
  mascot: 'default'
};

const THEME_VISUALS: Record<string, StudentThemeVisual> = {
  rainbow: {
    key: 'rainbow',
    bodyMode: 'light',
    layoutClass: 'student-layout-rainbow',
    chatClass: 'student-chat-rainbow',
    accent: '#b18bcf',
    primary: '#7e68b2',
    surface: '#fffefe',
    surfaceAlt: '#fffafd',
    border: 'rgba(197, 174, 226, 0.34)',
    chip: 'rgba(251, 245, 255, 0.96)',
    mascot: 'rainbow'
  },
  mango: {
    key: 'mango',
    bodyMode: 'light',
    layoutClass: 'student-layout-mango',
    chatClass: 'student-chat-mango',
    accent: '#f49d1a',
    primary: '#f6a623',
    surface: '#fffdf8',
    surfaceAlt: '#fff6e3',
    border: 'rgba(241, 179, 63, 0.3)',
    chip: 'rgba(255, 245, 223, 0.96)',
    mascot: 'mango'
  },
  rocket: {
    key: 'rocket',
    bodyMode: 'light',
    layoutClass: 'student-layout-rocket',
    chatClass: 'student-chat-rocket',
    accent: '#6a4bb7',
    primary: '#7b5cc8',
    surface: '#fcfbff',
    surfaceAlt: '#f1ecff',
    border: 'rgba(117, 91, 180, 0.32)',
    chip: 'rgba(242, 237, 255, 0.96)',
    mascot: 'rocket'
  },
  ice_cream: {
    key: 'ice_cream',
    bodyMode: 'light',
    layoutClass: 'student-layout-ice-cream',
    chatClass: 'student-chat-ice-cream',
    accent: '#d77ab0',
    primary: '#e48bbf',
    surface: '#fffdfd',
    surfaceAlt: '#fbf3ff',
    border: 'rgba(214, 145, 188, 0.28)',
    chip: 'rgba(251, 241, 251, 0.96)',
    mascot: 'ice-cream'
  }
};

export function normalizeStudentThemeName(name: string | null | undefined): string {
  const normalized = (name || '').trim().toLowerCase();
  return THEME_ALIAS_MAP[normalized] || normalized || 'default';
}

export function isJuniorStudentTheme(name: string | null | undefined): boolean {
  const normalized = normalizeStudentThemeName(name);
  return normalized === 'rainbow' || normalized === 'mango' || normalized === 'rocket' || normalized === 'ice_cream';
}

export function getStudentThemeVisual(name: string | null | undefined): StudentThemeVisual {
  const normalized = normalizeStudentThemeName(name);
  return THEME_VISUALS[normalized] || DEFAULT_VISUAL;
}
